import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import TarjetaReferidos from '../components/mijpm/TarjetaReferidos';
import SeccionTienda from '../components/mijpm/tienda/SeccionTienda';
import CompraOnlineDetalle from '../components/mijpm/tienda/CompraOnlineDetalle';
import {
  ShoppingBag,
  User,
  Package,
  CreditCard,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  LogOut,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Phone,
  Mail,
  DollarSign,
  MessageCircle,
  Download,
  FileText,
  Star,
  Gift,
  Info,
  X,
  History,
  Home,
  Award,
  CalendarDays,
  Sparkles,
  Lock,
  MapPin,
  Shirt,
  Timer,
  Check,
  Circle,
  Sun,
  Moon,
  Plus,
  Minus,
  Send,
  ClipboardList,
  ArrowLeft,
  ExternalLink,
  Trash2,
  Eye,
  EyeOff
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== THEME CONTEXT ====================
const ThemeContext = createContext({ isDark: true, toggleTheme: () => {} });

const useTheme = () => useContext(ThemeContext);

// Theme colors configuration
const getThemeColors = (isDark) => ({
  bg: isDark ? 'bg-[#0A0A0A]' : 'bg-gray-50',
  bgSecondary: isDark ? 'bg-[#121212]' : 'bg-white',
  card: isDark ? 'bg-[#18181B]' : 'bg-white',
  cardHover: isDark ? 'hover:bg-[#222226]' : 'hover:bg-gray-50',
  border: isDark ? 'border-[#27272A]' : 'border-gray-200',
  borderHover: isDark ? 'hover:border-[#3F3F46]' : 'hover:border-gray-300',
  text: isDark ? 'text-white' : 'text-gray-900',
  textSecondary: isDark ? 'text-zinc-400' : 'text-gray-600',
  textMuted: isDark ? 'text-zinc-500' : 'text-gray-500',
  input: isDark ? 'bg-[#0A0A0A] border-[#27272A]' : 'bg-gray-50 border-gray-200',
  inputFocus: isDark ? 'focus:border-[#C9A962]' : 'focus:border-[#C9A962]',
  sidebar: isDark ? 'bg-[#121212]' : 'bg-white',
  sidebarBorder: isDark ? 'border-zinc-800' : 'border-gray-200',
});

// ==================== UTILIDADES ====================
const formatPrice = (value) => {
  if (!value && value !== 0) return '$0';
  return `$${parseInt(value).toLocaleString('es-CO')}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'Por confirmar';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return 'Por confirmar';
  }
};

const formatDateLong = (dateStr) => {
  if (!dateStr) return 'Por confirmar';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return 'Por confirmar';
  }
};

const calcularDiasRestantes = (fechaStr) => {
  if (!fechaStr) return null;
  try {
    const fecha = new Date(fechaStr);
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    fecha.setHours(0, 0, 0, 0);
    const diff = fecha - hoy;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
};

// ==================== LOGIN COMPONENT ====================
function MiJPMLogin({ onLogin }) {
  const [correo, setCorreo] = useState('');
  const [password, setPassword] = useState('');
  const [verPassword, setVerPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/mi-jpm/login`, { correo, password });
      const { access_token, nombre, cliente_id } = response.data;
      
      localStorage.setItem('mi_jpm_token', access_token);
      localStorage.setItem('mi_jpm_nombre', nombre);
      localStorage.setItem('mi_jpm_cliente_id', cliente_id);
      
      toast.success(`¡Bienvenido, ${nombre}!`);
      onLogin(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Credenciales incorrectas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4" data-testid="mi-jpm-login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-10">
          <img
            src="/images/logo-perro-jpm.png"
            alt="Juan Pablo Murillo"
            className="mx-auto mb-5 h-auto w-28 sm:w-36 object-contain select-none"
            draggable={false}
            data-testid="mi-jpm-login-logo"
          />
          <h1 className="text-2xl font-serif font-medium tracking-wide text-white">JUAN PABLO MURILLO</h1>
          <p className="text-[#C9A962] mt-2 tracking-[0.2em] text-xs font-medium">MI JPM</p>
        </div>
        
        {/* Form */}
        <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-8">
          <h2 className="text-lg font-medium text-white mb-6 text-center">Accede a tu área privada</h2>
          
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="mi-jpm-login-form">
            <div>
              <label className="block text-xs text-zinc-400 mb-2 tracking-wide">CORREO ELECTRÓNICO</label>
              <input
                type="email"
                value={correo}
                onChange={(e) => setCorreo(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-4 py-3.5 text-white placeholder-zinc-600 focus:outline-none focus:border-[#C9A962] transition-colors"
                placeholder="tu@correo.com"
                required
                data-testid="mi-jpm-email-input"
              />
            </div>
            
            <div>
              <label className="block text-xs text-zinc-400 mb-2 tracking-wide">CONTRASEÑA</label>
              <div className="relative">
                <input
                  type={verPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl pl-4 pr-12 py-3.5 text-white placeholder-zinc-600 focus:outline-none focus:border-[#C9A962] transition-colors"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  data-testid="mi-jpm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setVerPassword(v => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg text-zinc-500 hover:text-[#C9A962] transition-colors"
                  aria-label={verPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  aria-pressed={verPassword}
                  data-testid="mi-jpm-toggle-password"
                >
                  {verPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#C9A962] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#C9A962] text-black font-semibold py-3.5 rounded-xl transition-all duration-300 disabled:opacity-50 mt-2"
              data-testid="mi-jpm-login-button"
            >
              {loading ? 'Ingresando...' : 'Ingresar a Mi JPM'}
            </button>
          </form>
          
          <p className="text-xs text-zinc-500 text-center mt-6">
            Tu contraseña inicial es tu correo electrónico
          </p>
        </div>
        
        {/* Footer */}
        <p className="text-center text-zinc-600 text-xs mt-8">
          © {new Date().getFullYear()} Juan Pablo Murillo. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
}

// ==================== KPI CARD COMPONENT ====================
function KPICard({ icon: Icon, label, value, subvalue, highlight, onClick, testId }) {
  return (
    <div
      onClick={onClick}
      className={`bg-[#18181B] border border-[#27272A] rounded-xl p-4 lg:p-5 ${onClick ? 'cursor-pointer hover:border-[#C9A962]/50 hover:bg-[#222226]' : ''} transition-all duration-200`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg ${highlight ? 'bg-[#C9A962]/20' : 'bg-zinc-800'}`}>
          <Icon className={`w-5 h-5 ${highlight ? 'text-[#C9A962]' : 'text-zinc-400'}`} />
        </div>
        {onClick && <ChevronRight className="w-4 h-4 text-zinc-600" />}
      </div>
      <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-xl lg:text-2xl font-medium ${highlight ? 'text-[#C9A962]' : 'text-white'}`}>{value}</p>
      {subvalue && <p className="text-xs text-zinc-400 mt-1">{subvalue}</p>}
    </div>
  );
}

// ==================== PROGRESS BAR COMPONENT ====================
function ProgressBar({ percentage, color = 'gold' }) {
  const bgColor = color === 'gold' ? 'bg-[#C9A962]' : color === 'green' ? 'bg-emerald-500' : 'bg-blue-500';
  return (
    <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
      <div 
        className={`h-full ${bgColor} rounded-full transition-all duration-500`}
        style={{ width: `${Math.min(percentage, 100)}%` }}
      />
    </div>
  );
}

// ==================== ORDER TIMELINE COMPONENT ====================
function OrderTimeline({ estado }) {
  const stages = [
    { key: 'confirmado', label: 'Confirmado', icon: Check },
    { key: 'produccion', label: 'En producción', icon: Shirt },
    { key: 'prueba', label: 'Prueba', icon: Calendar },
    { key: 'ajustes', label: 'Ajustes', icon: Sparkles },
    { key: 'entrega', label: 'Entrega', icon: Package }
  ];
  
  const getStageIndex = (estado) => {
    const estadoLower = (estado || '').toLowerCase();
    if (estadoLower.includes('entregad')) return 5;
    if (estadoLower.includes('ajuste')) return 4;
    if (estadoLower.includes('prueba')) return 3;
    if (estadoLower.includes('produccion') || estadoLower.includes('proceso')) return 2;
    return 1;
  };
  
  const currentStage = getStageIndex(estado);
  
  return (
    <div className="flex items-center justify-between w-full py-4">
      {stages.map((stage, index) => {
        const isCompleted = index + 1 < currentStage;
        const isCurrent = index + 1 === currentStage;
        const IconComponent = stage.icon;
        
        return (
          <React.Fragment key={stage.key}>
            <div className="flex flex-col items-center gap-2 flex-1">
              <div className={`
                w-8 h-8 lg:w-10 lg:h-10 rounded-full flex items-center justify-center transition-all
                ${isCompleted ? 'bg-[#C9A962] text-black' : isCurrent ? 'bg-[#C9A962]/20 border-2 border-[#C9A962] text-[#C9A962]' : 'bg-zinc-800 text-zinc-500'}
              `}>
                {isCompleted ? <Check className="w-4 h-4 lg:w-5 lg:h-5" /> : <IconComponent className="w-4 h-4 lg:w-5 lg:h-5" />}
              </div>
              <span className={`text-[10px] lg:text-xs text-center ${isCurrent ? 'text-[#C9A962] font-medium' : isCompleted ? 'text-zinc-300' : 'text-zinc-500'}`}>
                {stage.label}
              </span>
            </div>
            {index < stages.length - 1 && (
              <div className={`h-0.5 flex-1 max-w-[40px] lg:max-w-[60px] -mt-6 ${isCompleted ? 'bg-[#C9A962]' : 'bg-zinc-700'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ==================== PAYMENT SCHEDULE COMPONENT ====================
function PaymentSchedule({ planPagos, totalPagado }) {
  if (!planPagos || planPagos.length === 0) return null;
  
  return (
    <div className="space-y-3">
      {planPagos.map((cuota, index) => {
        const isPaid = cuota.estado === 'pagada' || cuota.saldo_pendiente === 0;
        const isPartial = cuota.saldo_pendiente > 0 && cuota.saldo_pendiente < (cuota.valor_programado || cuota.valor);
        
        return (
          <div 
            key={index}
            className={`flex items-center justify-between p-3 rounded-lg border ${
              isPaid ? 'bg-emerald-500/5 border-emerald-500/20' : isPartial ? 'bg-[#C9A962]/5 border-[#C9A962]/20' : 'bg-zinc-800/50 border-zinc-700'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                isPaid ? 'bg-emerald-500/20 text-emerald-400' : isPartial ? 'bg-[#C9A962]/20 text-[#C9A962]' : 'bg-zinc-700 text-zinc-400'
              }`}>
                {isPaid ? <Check className="w-4 h-4" /> : <span className="text-sm font-medium">{index + 1}</span>}
              </div>
              <div>
                <p className="text-sm text-white">Cuota {index + 1}</p>
                <p className="text-xs text-zinc-500">{formatDate(cuota.fecha_acordada || cuota.fecha)}</p>
              </div>
            </div>
            <div className="text-right">
              <p className={`text-sm font-medium ${isPaid ? 'text-emerald-400' : 'text-white'}`}>
                {formatPrice(cuota.valor_programado || cuota.valor)}
              </p>
              <p className={`text-xs ${isPaid ? 'text-emerald-400' : isPartial ? 'text-[#C9A962]' : 'text-zinc-500'}`}>
                {isPaid ? 'Pagada' : isPartial ? `Pendiente: ${formatPrice(cuota.saldo_pendiente)}` : 'Pendiente'}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ==================== ORDER CARD COMPONENT ====================
function OrderCard({ pedido, onClick, expanded, onToggle, isDark, onActualizar }) {
  const diasRestantes = calcularDiasRestantes(pedido.fecha_entrega);
  const total = pedido.total || pedido.total_final || 0;
  const porcentajePagado = total > 0 ? Math.round((pedido.total_pagado / total) * 100) : 0;
  
  // Extract garment names from prendas or items
  const prendas = (pedido.prendas || pedido.items || []).map(item => {
    if (typeof item === 'string') return item.split(',')[0].split('.')[0].trim();
    const nombre = item.descripcion || item.descripcion_manual || item.prenda || 'Prenda';
    return nombre.split(',')[0].split('.')[0].trim();
  }).filter((v, i, a) => a.indexOf(v) === i).slice(0, 5);

  const getEstadoBadge = (estado) => {
    const estadoLower = (estado || '').toLowerCase();
    if (estadoLower.includes('entregad')) return { text: 'Entregado', class: 'bg-emerald-500/20 text-emerald-400' };
    if (estadoLower.includes('proceso') || estadoLower.includes('produccion')) return { text: 'En proceso', class: 'bg-blue-500/20 text-blue-400' };
    if (estadoLower.includes('pendiente')) return { text: 'Pendiente', class: 'bg-[#C9A962]/20 text-[#C9A962]' };
    return { text: estado || 'En proceso', class: 'bg-zinc-700 text-zinc-300' };
  };

  const estadoBadge = getEstadoBadge(pedido.estado_texto || pedido.estado_venta || pedido.estado);

  return (
    <div 
      data-testid={`mijpm-pedido-${pedido.numero}`}
      className="bg-[#18181B] border border-[#27272A] rounded-2xl overflow-hidden hover:border-[#3F3F46] transition-all duration-200"
    >
      {/* Header */}
      <div 
        className="p-4 lg:p-5 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[#C9A962] font-mono text-sm font-medium">{pedido.numero}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${estadoBadge.class}`}>
                {estadoBadge.text}
              </span>
              {pedido.compra_online && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300" data-testid={`badge-online-${pedido.numero}`}>Compra online</span>
              )}
            </div>
            <p className="text-zinc-400 text-xs">Pedido realizado el {formatDate(pedido.fecha_compra || pedido.fecha_venta || pedido.created_at)}</p>
          </div>
          <button className="p-1 hover:bg-zinc-800 rounded-lg transition-colors">
            {expanded ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
          </button>
        </div>

        {/* COUNTDOWN - Big prominent countdown when there are days remaining */}
        {diasRestantes !== null && diasRestantes >= 0 && (
          <div className="bg-gradient-to-r from-[#C9A962]/20 to-[#C9A962]/5 border border-[#C9A962]/30 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-[#C9A962]/20 flex items-center justify-center">
                  <Timer className="w-7 h-7 text-[#C9A962]" />
                </div>
                <div>
                  <p className="text-xs text-[#C9A962] uppercase tracking-wider">Tiempo para tu entrega</p>
                  <p className="text-2xl font-bold text-white">
                    {diasRestantes} <span className="text-lg font-normal text-zinc-400">{diasRestantes === 1 ? 'día' : 'días'}</span>
                  </p>
                </div>
              </div>
              <div className="text-right hidden sm:block">
                <p className="text-xs text-zinc-500">Entrega programada</p>
                <p className="text-sm text-white font-medium">{formatDate(pedido.fecha_entrega)}</p>
              </div>
            </div>
            {/* Progress bar visual */}
            <div className="mt-3">
              <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#C9A962] to-[#D4AF37] rounded-full transition-all duration-1000"
                  style={{ width: `${Math.max(5, Math.min(100, 100 - (diasRestantes / 90) * 100))}%` }}
                />
              </div>
              <p className="text-[10px] text-zinc-500 mt-1 text-center">
                {diasRestantes <= 7 ? '¡Ya casi está listo!' : diasRestantes <= 30 ? 'En las próximas semanas' : 'En proceso de confección'}
              </p>
            </div>
          </div>
        )}

        {/* If no delivery date, show pending message */}
        {(diasRestantes === null || pedido.dias_para_entrega === null) && !pedido.fecha_entrega && (
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-zinc-500" />
              <div>
                <p className="text-sm text-zinc-300">Fecha de entrega por confirmar</p>
                <p className="text-xs text-zinc-500">Te notificaremos cuando se programe tu entrega</p>
              </div>
            </div>
          </div>
        )}

        {/* Quick Info */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Fecha del evento</p>
            <p className="text-sm text-white">{formatDate(pedido.fecha_evento)}</p>
          </div>
          <div>
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Entrega programada</p>
            <p className="text-sm text-white">{formatDate(pedido.fecha_entrega)}</p>
          </div>
        </div>

        {/* Payment Progress */}
        <div className="bg-zinc-800/50 rounded-xl p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-zinc-400">Estado del pago</span>
            <span className="text-xs text-[#C9A962] font-medium">{porcentajePagado}% pagado</span>
          </div>
          <ProgressBar percentage={porcentajePagado} color={porcentajePagado >= 100 ? 'green' : 'gold'} />
          <div className="flex items-center justify-between mt-2 text-xs">
            <span className="text-zinc-500">{formatPrice(pedido.total_pagado)} pagado</span>
            <span className="text-white font-medium">Total: {formatPrice(total)}</span>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-[#27272A] p-4 lg:p-5 space-y-6 animate-in slide-in-from-top-2 duration-200">
          {pedido.compra_online && <CompraOnlineDetalle pedido={pedido} onActualizar={onActualizar} />}
          {/* Timeline */}
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Progreso del pedido</p>
            <OrderTimeline estado={pedido.estado_texto || pedido.estado_venta || pedido.estado} />
          </div>

          {/* Garments */}
          {prendas.length > 0 && (
            <div>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Tu pedido incluye</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {prendas.map((prenda, i) => (
                  <div key={i} className="flex items-center gap-2 bg-zinc-800/50 rounded-lg px-3 py-2">
                    <Shirt className="w-4 h-4 text-[#C9A962]" />
                    <span className="text-sm text-zinc-300">{prenda}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment Details */}
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Detalle de pagos</p>
            <div className="bg-zinc-800/30 rounded-xl p-4 space-y-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase mb-1">Total</p>
                  <p className="text-lg text-white font-medium">{formatPrice(total)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase mb-1">Pagado</p>
                  <p className="text-lg text-emerald-400 font-medium">{formatPrice(pedido.total_pagado)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase mb-1">Saldo</p>
                  <p className="text-lg text-[#C9A962] font-medium">{formatPrice(pedido.saldo_pendiente)}</p>
                </div>
              </div>
              
              {pedido.plan_pagos && pedido.plan_pagos.length > 0 && (
                <div className="pt-4 border-t border-zinc-700">
                  <p className="text-xs text-zinc-500 mb-3">Plan de cuotas</p>
                  <PaymentSchedule planPagos={pedido.plan_pagos} totalPagado={pedido.total_pagado} />
                </div>
              )}
            </div>
          </div>

          {/* Event & Delivery */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-zinc-800/50 to-zinc-800/30 rounded-xl p-4 border border-zinc-700/50">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-[#C9A962]" />
                <span className="text-[10px] text-[#C9A962] uppercase tracking-wider">Tu evento</span>
              </div>
              <p className="text-white font-medium">{formatDateLong(pedido.fecha_evento)}</p>
            </div>
            <div className="bg-gradient-to-br from-[#C9A962]/10 to-[#C9A962]/5 rounded-xl p-4 border border-[#C9A962]/20">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-4 h-4 text-[#C9A962]" />
                <span className="text-[10px] text-[#C9A962] uppercase tracking-wider">Entrega</span>
              </div>
              <p className="text-white font-medium">{formatDateLong(pedido.fecha_entrega)}</p>
              {diasRestantes !== null && diasRestantes > 0 && (
                <p className="text-[#C9A962] text-sm mt-1">Faltan {diasRestantes} días</p>
              )}
            </div>
          </div>

          {/* View Full Details Button */}
          <button
            onClick={() => onClick(pedido)}
            className="w-full bg-zinc-800 hover:bg-zinc-700 text-white py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
            data-testid={`view-order-${pedido.numero}`}
          >
            <FileText className="w-4 h-4" />
            Ver documento completo
          </button>
        </div>
      )}
    </div>
  );
}

// ==================== POINTS MODAL ====================
function PuntosModal({ isOpen, onClose }) {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-[#18181B] border border-[#27272A] rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-[#27272A] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#C9A962]/20 rounded-lg">
              <Star className="w-5 h-5 text-[#C9A962]" fill="#C9A962" />
            </div>
            <h3 className="text-lg font-medium text-white">¿Cómo funcionan los Puntos JPM?</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg transition-colors">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#C9A962]/20 flex items-center justify-center flex-shrink-0">
                <span className="text-[#C9A962] font-medium">1</span>
              </div>
              <div>
                <p className="text-white font-medium mb-1">Acumula con tus compras</p>
                <p className="text-sm text-zinc-400">Por cada $10.000 COP pagados en compras nuevas, acumulas 1 Punto JPM.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#C9A962]/20 flex items-center justify-center flex-shrink-0">
                <span className="text-[#C9A962] font-medium">2</span>
              </div>
              <div>
                <p className="text-white font-medium mb-1">Valor de tus puntos</p>
                <p className="text-sm text-zinc-400">Cada Punto JPM equivale a $100 COP de beneficio para tu próxima compra.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#C9A962]/20 flex items-center justify-center flex-shrink-0">
                <span className="text-[#C9A962] font-medium">3</span>
              </div>
              <div>
                <p className="text-white font-medium mb-1">Redime en futuras compras</p>
                <p className="text-sm text-zinc-400">Utiliza tus puntos como descuento en tu próxima compra nueva con nosotros.</p>
              </div>
            </div>
          </div>
          
          <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
            <p className="text-xs text-[#C9A962] uppercase tracking-wider mb-2">Importante</p>
            <ul className="text-xs text-zinc-400 space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-[#C9A962]">•</span>
                Los puntos solo aplican en compras nuevas
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#C9A962]">•</span>
                No aplican a pedidos actualmente en curso
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#C9A962]">•</span>
                No son canjeables por dinero en efectivo
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#C9A962]">•</span>
                No generan devolución de dinero
              </li>
            </ul>
          </div>
        </div>
        
        <div className="p-6 border-t border-[#27272A]">
          <button
            onClick={onClose}
            className="w-full bg-[#C9A962] hover:bg-[#D4AF37] text-black font-medium py-3 rounded-xl transition-colors"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== POINTS SECTION ====================
function PuntosSection({ puntos, onShowInfo }) {
  if (!puntos?.programa_activo) return null;
  
  const maxPuntos = Math.max(puntos.puntos_disponibles, 1000);
  const porcentaje = (puntos.puntos_disponibles / maxPuntos) * 100;
  
  return (
    <div className="bg-gradient-to-br from-[#18181B] to-[#18181B]/80 border border-[#C9A962]/30 rounded-2xl p-5 lg:p-6 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 opacity-5">
        <Star className="w-full h-full text-[#C9A962]" fill="#C9A962" />
      </div>
      
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-[#C9A962]" fill="#C9A962" />
            <span className="text-[#C9A962] font-medium tracking-wider text-sm">PUNTOS JPM</span>
          </div>
          <button
            onClick={onShowInfo}
            className="text-xs text-zinc-400 hover:text-white flex items-center gap-1 transition-colors"
            data-testid="puntos-info-button"
          >
            <Info className="w-3.5 h-3.5" />
            ¿Cómo funcionan?
          </button>
        </div>
        
        <div className="flex items-baseline gap-2 mb-4">
          <span className="text-4xl lg:text-5xl font-light text-white" data-testid="puntos-cantidad">
            {puntos.puntos_disponibles}
          </span>
          <span className="text-zinc-400">puntos disponibles</span>
        </div>
        
        {/* Progress bar */}
        <div className="mb-4">
          <ProgressBar percentage={porcentaje} color="gold" />
        </div>
        
        {puntos.puntos_disponibles > 0 ? (
          <div className="bg-[#C9A962]/10 border border-[#C9A962]/20 rounded-xl p-4">
            <p className="text-xs text-zinc-400 mb-1">Beneficio disponible para tu próxima compra</p>
            <p className="text-2xl font-medium text-[#C9A962]" data-testid="puntos-valor">
              {formatPrice(puntos.valor_beneficio)} COP
            </p>
          </div>
        ) : (
          <div className="bg-zinc-800/50 rounded-xl p-4">
            <p className="text-sm text-zinc-400">
              Realiza una compra nueva para comenzar a acumular beneficios.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== POINTS HISTORY COMPONENT ====================
function PuntosHistorial({ historial, loading }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-8 h-8 border-2 border-[#C9A962]/30 border-t-[#C9A962] rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!historial || historial.length === 0) {
    return (
      <div className="bg-zinc-800/30 rounded-xl p-6 text-center">
        <History className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-400 text-sm">Aún no tienes movimientos de puntos</p>
        <p className="text-zinc-500 text-xs mt-1">Tu historial aparecerá aquí cuando realices compras</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      {historial.map((mov, index) => {
        const isPositive = mov.puntos > 0;
        const fecha = mov.fecha ? new Date(mov.fecha).toLocaleDateString('es-CO', {
          day: 'numeric', month: 'short', year: 'numeric'
        }) : '';
        
        return (
          <div 
            key={index}
            className="flex items-center justify-between bg-zinc-800/30 rounded-xl p-4 border border-zinc-700/50"
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isPositive ? 'bg-emerald-500/20' : 'bg-red-500/20'
              }`}>
                {isPositive ? (
                  <Star className="w-5 h-5 text-emerald-400" />
                ) : (
                  <Gift className="w-5 h-5 text-red-400" />
                )}
              </div>
              <div>
                <p className="text-sm text-white font-medium">{mov.concepto}</p>
                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  <span>{fecha}</span>
                  {mov.venta_numero && (
                    <>
                      <span>•</span>
                      <span className="text-zinc-400">{mov.venta_numero}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className={`text-right ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
              <p className="text-lg font-medium">{mov.puntos_display}</p>
              <p className="text-xs opacity-70">puntos</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ==================== APPOINTMENTS/FITTINGS SECTION ====================
function CitasPruebasSection({ citas, pedidos }) {
  // Extract upcoming fittings from orders
  const proximasPruebas = [];
  
  pedidos.forEach(pedido => {
    if (pedido.citas && Array.isArray(pedido.citas)) {
      pedido.citas.forEach(cita => {
        if (cita.fecha) {
          proximasPruebas.push({
            ...cita,
            pedido_numero: pedido.numero,
            tipo: cita.tipo || 'Cita'
          });
        }
      });
    }
    if (pedido.pruebas && Array.isArray(pedido.pruebas)) {
      pedido.pruebas.forEach(prueba => {
        if (prueba.fecha) {
          proximasPruebas.push({
            ...prueba,
            pedido_numero: pedido.numero,
            tipo: prueba.tipo || 'Prueba de prendas'
          });
        }
      });
    }
  });
  
  // Sort by date
  proximasPruebas.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
  
  // Filter only future dates
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const citasFuturas = proximasPruebas.filter(c => new Date(c.fecha) >= hoy);
  
  if (citasFuturas.length === 0) {
    return (
      <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <CalendarDays className="w-5 h-5 text-[#C9A962]" />
          <h3 className="text-lg font-medium text-white">Mis Citas y Pruebas</h3>
        </div>
        <div className="bg-zinc-800/30 rounded-xl p-6 text-center">
          <Calendar className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400 text-sm">No tienes citas programadas</p>
          <p className="text-zinc-500 text-xs mt-1">Te notificaremos cuando se programe tu próxima prueba</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-5 lg:p-6">
      <div className="flex items-center gap-3 mb-4">
        <CalendarDays className="w-5 h-5 text-[#C9A962]" />
        <h3 className="text-lg font-medium text-white">Mis Citas y Pruebas</h3>
      </div>
      
      <div className="space-y-3">
        {citasFuturas.map((cita, index) => {
          const fecha = new Date(cita.fecha);
          const diasRestantes = Math.ceil((fecha - new Date()) / (1000 * 60 * 60 * 24));
          
          return (
            <div 
              key={index}
              className="bg-gradient-to-r from-[#C9A962]/10 to-[#C9A962]/5 border border-[#C9A962]/20 rounded-xl p-4"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[#C9A962] font-medium">{cita.tipo}</p>
                  <p className="text-white text-lg font-medium mt-1">
                    {fecha.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </p>
                  {cita.hora && (
                    <p className="text-zinc-400 text-sm flex items-center gap-1 mt-1">
                      <Clock className="w-3.5 h-3.5" />
                      {cita.hora}
                    </p>
                  )}
                  {cita.ubicacion && (
                    <p className="text-zinc-500 text-xs flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" />
                      {cita.ubicacion}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xs text-zinc-500">Pedido</p>
                  <p className="text-sm text-[#C9A962] font-mono">{cita.pedido_numero}</p>
                  {diasRestantes > 0 && (
                    <p className="text-xs text-zinc-400 mt-2">
                      en {diasRestantes} {diasRestantes === 1 ? 'día' : 'días'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ==================== PROFILE SECTION ====================
function ProfileSection({ perfil }) {
  return (
    <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-5 lg:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C9A962] to-[#8B7355] flex items-center justify-center">
          <User className="w-6 h-6 text-black" />
        </div>
        <div>
          <h3 className="text-lg font-medium text-white">{perfil?.nombre}</h3>
          <p className="text-xs text-[#C9A962] uppercase tracking-wider">Cliente JPM</p>
        </div>
      </div>
      
      <div className="space-y-4">
        <div>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Correo / Usuario de Mi JPM</p>
          <div className="flex items-center gap-2 bg-zinc-800/50 rounded-lg px-3 py-2.5">
            <Mail className="w-4 h-4 text-zinc-500" />
            <span className="text-sm text-zinc-300 flex-1">{perfil?.correo || perfil?.email}</span>
            <Lock className="w-3.5 h-3.5 text-zinc-600" />
          </div>
          <p className="text-[10px] text-zinc-600 mt-1">Para modificar este dato comunícate con nuestro equipo</p>
        </div>
        
        {perfil?.telefono && (
          <div>
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Teléfono</p>
            <div className="flex items-center gap-2 bg-zinc-800/50 rounded-lg px-3 py-2.5">
              <Phone className="w-4 h-4 text-zinc-500" />
              <span className="text-sm text-zinc-300">{perfil.telefono}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== SOLICITUD DE COTIZACIÓN ====================

const PRODUCTOS_ICONOS = {
  traje: Shirt,
  saco: Shirt,
  pantalon: Shirt,
  chaleco: Shirt,
  camisa: Shirt,
  zapatos: Package,
  accesorios: Gift
};

// Lista de productos disponibles para cotizar
const PRODUCTOS_COTIZAR = [
  { id: 'traje', nombre: 'Traje completo', descripcion: 'Saco y pantalón a la medida' },
  { id: 'saco', nombre: 'Saco', descripcion: 'Saco a la medida' },
  { id: 'pantalon', nombre: 'Pantalón', descripcion: 'Pantalón de vestir a la medida' },
  { id: 'chaleco', nombre: 'Chaleco', descripcion: 'Chaleco a la medida' },
  { id: 'camisa', nombre: 'Camisa', descripcion: 'Camisa a la medida' },
  { id: 'zapatos', nombre: 'Zapatos', descripcion: 'Calzado formal' },
  { id: 'accesorios', nombre: 'Accesorios', descripcion: 'Corbatas, pañuelos, mancornas, etc.' }
];

// Número de WhatsApp de la tienda
const WHATSAPP_TIENDA = '573223078959';

/**
 * Genera el mensaje de WhatsApp con toda la información de la consulta
 */
const generarMensajeWhatsApp = (perfil, productos, productosDisponibles, fechaEvento, observaciones) => {
  const nombreCliente = perfil?.nombre || 'Cliente';
  
  let mensaje = `Hola, soy ${nombreCliente}. Estoy interesado en cotizar estos productos:\n\n`;
  
  productos.forEach((prod) => {
    const productoInfo = productosDisponibles.find(p => p.id === prod.tipo);
    const nombreProducto = productoInfo?.nombre || prod.tipo;
    mensaje += `• ${prod.cantidad} × ${nombreProducto}\n`;
    if (prod.notas && prod.notas.trim()) {
      mensaje += `  Detalles: ${prod.notas.trim()}\n`;
    }
    mensaje += '\n';
  });
  
  if (fechaEvento) {
    const fecha = new Date(fechaEvento + 'T00:00:00');
    const opciones = { day: 'numeric', month: 'long', year: 'numeric' };
    const fechaFormateada = fecha.toLocaleDateString('es-CO', opciones);
    mensaje += `Fecha en que los necesito: ${fechaFormateada}\n\n`;
  }
  
  if (observaciones && observaciones.trim()) {
    mensaje += `Notas adicionales: ${observaciones.trim()}\n\n`;
  }
  
  mensaje += `Quedo atento a que un asesor de Juan Pablo Murillo me contacte. Gracias.`;
  
  return mensaje;
};

/**
 * Abre WhatsApp con el mensaje preparado
 */
const abrirWhatsAppCotizacion = (perfil, productos, productosDisponibles, fechaEvento, observaciones) => {
  const mensaje = generarMensajeWhatsApp(perfil, productos, productosDisponibles, fechaEvento, observaciones);
  const mensajeEncoded = encodeURIComponent(mensaje);
  const url = `https://wa.me/${WHATSAPP_TIENDA}?text=${mensajeEncoded}`;
  window.open(url, '_blank');
};

// ==================== CONSULTAR COTIZACIÓN (NUEVO FLUJO SIMPLIFICADO) ====================
function ConsultarCotizacionForm({ onBack, perfil }) {
  const { isDark } = useTheme();
  const theme = getThemeColors(isDark);
  
  const [productos, setProductos] = useState([]);
  const [fechaEvento, setFechaEvento] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [step, setStep] = useState(1); // 1: selección, 2: resumen
  
  const toggleProducto = (productoId) => {
    const existe = productos.find(p => p.tipo === productoId);
    if (existe) {
      setProductos(productos.filter(p => p.tipo !== productoId));
    } else {
      setProductos([...productos, { tipo: productoId, cantidad: 1, notas: '' }]);
    }
  };
  
  const updateProducto = (productoId, field, value) => {
    setProductos(productos.map(p => 
      p.tipo === productoId ? { ...p, [field]: value } : p
    ));
  };
  
  const incrementarCantidad = (productoId) => {
    updateProducto(productoId, 'cantidad', (productos.find(p => p.tipo === productoId)?.cantidad || 1) + 1);
  };
  
  const decrementarCantidad = (productoId) => {
    const actual = productos.find(p => p.tipo === productoId)?.cantidad || 1;
    if (actual > 1) {
      updateProducto(productoId, 'cantidad', actual - 1);
    }
  };
  
  const getProductoNombre = (tipo) => {
    return PRODUCTOS_COTIZAR.find(p => p.id === tipo)?.nombre || tipo;
  };
  
  const handleWhatsApp = () => {
    abrirWhatsAppCotizacion(perfil, productos, PRODUCTOS_COTIZAR, fechaEvento, observaciones);
  };
  
  // Step 1: Selección de productos
  if (step === 1) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={onBack} className={`p-2 rounded-lg ${theme.card} ${theme.border} border`}>
            <ArrowLeft className="w-5 h-5 text-zinc-400" />
          </button>
          <div>
            <h2 className={`text-xl font-serif font-medium ${theme.text}`}>Consultar Cotización</h2>
            <p className={`text-sm ${theme.textSecondary}`}>Selecciona los productos que te interesan</p>
          </div>
        </div>
        
        {/* Productos */}
        <div className={`${theme.card} border ${theme.border} rounded-2xl p-5`}>
          <p className={`text-sm ${theme.textSecondary} mb-4`}>Selecciona los productos que deseas cotizar:</p>
          
          <div className="space-y-3">
            {PRODUCTOS_COTIZAR.map((producto) => {
              const seleccionado = productos.find(p => p.tipo === producto.id);
              const Icon = PRODUCTOS_ICONOS[producto.id] || Package;
              
              return (
                <div 
                  key={producto.id} 
                  data-testid={`producto-card-${producto.id}`}
                  onClick={() => !seleccionado && toggleProducto(producto.id)}
                  className={`border ${seleccionado ? 'border-[#C9A962] bg-[#C9A962]/5' : theme.border} rounded-xl p-4 transition-all cursor-pointer hover:border-[#C9A962]/50`}
                >
                  <div className="flex items-start gap-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleProducto(producto.id); }}
                      className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                        seleccionado ? 'bg-[#C9A962] border-[#C9A962]' : `${theme.border} border`
                      }`}
                    >
                      {seleccionado && <Check className="w-4 h-4 text-black" />}
                    </button>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${seleccionado ? 'text-[#C9A962]' : theme.textSecondary}`} />
                        <p className={`font-medium ${theme.text}`}>{producto.nombre}</p>
                      </div>
                      <p className={`text-xs ${theme.textMuted} mt-0.5`}>{producto.descripcion}</p>
                      
                      {/* Opciones si está seleccionado */}
                      {seleccionado && (
                        <div className="mt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                          {/* Cantidad */}
                          <div className="flex items-center gap-3">
                            <span className={`text-xs ${theme.textSecondary}`}>Cantidad:</span>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => decrementarCantidad(producto.id)}
                                className={`w-8 h-8 rounded-lg ${isDark ? 'bg-zinc-800' : 'bg-zinc-200'} flex items-center justify-center`}
                              >
                                <Minus className={`w-4 h-4 ${theme.textSecondary}`} />
                              </button>
                              <span className={`w-8 text-center font-medium ${theme.text}`}>{seleccionado.cantidad}</span>
                              <button
                                onClick={() => incrementarCantidad(producto.id)}
                                className={`w-8 h-8 rounded-lg ${isDark ? 'bg-zinc-800' : 'bg-zinc-200'} flex items-center justify-center`}
                              >
                                <Plus className={`w-4 h-4 ${theme.textSecondary}`} />
                              </button>
                            </div>
                          </div>
                          
                          {/* Notas - CORREGIDO para visibilidad */}
                          <div>
                            <label className={`text-xs ${theme.textSecondary} block mb-1`}>¿Qué buscas? (opcional)</label>
                            <textarea
                              value={seleccionado.notas}
                              onChange={(e) => updateProducto(producto.id, 'notas', e.target.value)}
                              placeholder="Ej: Color azul marino, corte italiano, para evento formal..."
                              className={`w-full rounded-lg px-3 py-2 text-sm resize-none border transition-all
                                ${isDark 
                                  ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500 focus:border-[#C9A962] focus:ring-1 focus:ring-[#C9A962]/30' 
                                  : 'bg-white border-zinc-300 text-zinc-900 placeholder-zinc-400 focus:border-[#C9A962] focus:ring-1 focus:ring-[#C9A962]/30'
                                }`}
                              rows={2}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <span className={`text-xs ${theme.textMuted} whitespace-nowrap`}>Precio por cotizar</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Fecha del evento - CORREGIDO para visibilidad */}
        <div className={`${theme.card} border ${theme.border} rounded-2xl p-5`}>
          <label className={`text-sm font-medium ${theme.text} block mb-2`}>
            <Calendar className="w-4 h-4 inline-block mr-2 text-[#C9A962]" />
            Fecha de tu evento o entrega deseada (opcional)
          </label>
          <input
            type="date"
            value={fechaEvento}
            onChange={(e) => setFechaEvento(e.target.value)}
            className={`w-full rounded-xl px-4 py-3 border transition-all
              ${isDark 
                ? 'bg-zinc-800 border-zinc-700 text-white focus:border-[#C9A962] focus:ring-1 focus:ring-[#C9A962]/30 [color-scheme:dark]' 
                : 'bg-white border-zinc-300 text-zinc-900 focus:border-[#C9A962] focus:ring-1 focus:ring-[#C9A962]/30'
              }`}
          />
        </div>
        
        {/* Observaciones generales - CORREGIDO para visibilidad */}
        <div className={`${theme.card} border ${theme.border} rounded-2xl p-5`}>
          <label className={`text-sm font-medium ${theme.text} block mb-2`}>
            <FileText className="w-4 h-4 inline-block mr-2 text-[#C9A962]" />
            Observaciones generales (opcional)
          </label>
          <textarea
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            placeholder="¿Hay algo más que debamos saber sobre tu consulta?"
            className={`w-full rounded-xl px-4 py-3 border transition-all resize-none
              ${isDark 
                ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500 focus:border-[#C9A962] focus:ring-1 focus:ring-[#C9A962]/30' 
                : 'bg-white border-zinc-300 text-zinc-900 placeholder-zinc-400 focus:border-[#C9A962] focus:ring-1 focus:ring-[#C9A962]/30'
              }`}
            rows={3}
          />
        </div>
        
        {/* Botón revisar */}
        <button
          onClick={() => setStep(2)}
          disabled={productos.length === 0}
          data-testid="btn-revisar-solicitud"
          className={`w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
            productos.length > 0
              ? 'bg-gradient-to-r from-[#C9A962] to-[#D4AF37] text-black hover:from-[#D4AF37] hover:to-[#C9A962]'
              : 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
          }`}
        >
          Revisar mi consulta
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    );
  }
  
  // Step 2: Resumen editable y WhatsApp
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => setStep(1)} className={`p-2 rounded-lg ${theme.card} ${theme.border} border`}>
          <ArrowLeft className="w-5 h-5 text-zinc-400" />
        </button>
        <div>
          <h2 className={`text-xl font-serif font-medium ${theme.text}`}>Resumen de tu consulta</h2>
          <p className={`text-sm ${theme.textSecondary}`}>Revisa y consulta por WhatsApp</p>
        </div>
      </div>
      
      {/* Resumen de productos */}
      <div className={`${theme.card} border ${theme.border} rounded-2xl overflow-hidden`}>
        <div className={`p-4 border-b ${isDark ? 'border-zinc-700' : 'border-zinc-200'}`}>
          <h3 className={`font-medium ${theme.text} flex items-center gap-2`}>
            <ClipboardList className="w-4 h-4 text-[#C9A962]" />
            Productos seleccionados ({productos.length})
          </h3>
        </div>
        <div className={`divide-y ${isDark ? 'divide-zinc-700' : 'divide-zinc-200'}`}>
          {productos.map((producto) => (
            <div key={producto.tipo} className="p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <p className={`font-medium ${theme.text}`}>{getProductoNombre(producto.tipo)}</p>
                  <p className={`text-sm ${theme.textSecondary}`}>Cantidad: {producto.cantidad}</p>
                  {producto.notas && (
                    <p className={`text-xs ${theme.textMuted} mt-1 italic`}>"{producto.notas}"</p>
                  )}
                </div>
                <span className="text-[#C9A962] text-sm">Por cotizar</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Fecha y observaciones */}
      {(fechaEvento || observaciones) && (
        <div className={`${theme.card} border ${theme.border} rounded-2xl p-4 space-y-3`}>
          {fechaEvento && (
            <div>
              <p className={`text-xs ${theme.textSecondary}`}>Fecha del evento/entrega</p>
              <p className={`${theme.text} flex items-center gap-2`}>
                <Calendar className="w-4 h-4 text-[#C9A962]" />
                {formatDateLong(fechaEvento)}
              </p>
            </div>
          )}
          {observaciones && (
            <div>
              <p className={`text-xs ${theme.textSecondary}`}>Observaciones</p>
              <p className={`text-sm ${theme.text}`}>{observaciones}</p>
            </div>
          )}
        </div>
      )}
      
      {/* Datos del cliente */}
      <div className={`${theme.card} border ${theme.border} rounded-2xl p-4`}>
        <p className={`text-xs ${theme.textSecondary} mb-2`}>Consulta a nombre de:</p>
        <p className={`font-medium ${theme.text}`}>{perfil?.nombre}</p>
        <p className={`text-sm ${theme.textSecondary}`}>{perfil?.correo}</p>
      </div>
      
      {/* Aviso importante */}
      <div className={`${isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'} border rounded-xl p-4`}>
        <p className={`${isDark ? 'text-blue-400' : 'text-blue-600'} text-sm flex items-start gap-2`}>
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          Al pulsar "Consultar por WhatsApp" se abrirá WhatsApp con el mensaje preparado. Podrás revisarlo antes de enviarlo.
        </p>
      </div>
      
      {/* Botones */}
      <div className="space-y-3">
        {/* WhatsApp - Botón principal */}
        <button
          onClick={handleWhatsApp}
          data-testid="btn-whatsapp-consulta"
          className="w-full py-4 rounded-xl font-semibold bg-emerald-600 hover:bg-emerald-500 text-white transition-all flex items-center justify-center gap-2"
        >
          <MessageCircle className="w-5 h-5" />
          Consultar por WhatsApp
          <ExternalLink className="w-4 h-4" />
        </button>
        
        {/* Modificar */}
        <button
          onClick={() => setStep(1)}
          className={`w-full py-3 rounded-xl font-medium ${theme.card} border ${theme.border} ${theme.text} transition-all`}
        >
          Modificar mi consulta
        </button>
        
        {/* Volver */}
        <button
          onClick={onBack}
          className={`w-full py-3 rounded-xl font-medium text-zinc-500 hover:text-zinc-400 transition-all`}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

// ==================== SECCIÓN COTIZAR (SIMPLIFICADA) ====================
function SeccionCotizar({ perfil }) {
  const { isDark } = useTheme();
  const theme = getThemeColors(isDark);
  const [showForm, setShowForm] = useState(false);
  
  if (showForm) {
    return (
      <ConsultarCotizacionForm 
        onBack={() => setShowForm(false)}
        perfil={perfil}
      />
    );
  }
  
  return (
    <div className="space-y-6">
      <h2 className={`text-xl font-serif font-medium ${theme.text}`}>Consultar Cotización</h2>
      
      {/* Card principal */}
      <div className={`${theme.card} border ${theme.border} rounded-2xl p-6 text-center`}>
        <div className="w-16 h-16 rounded-full bg-[#C9A962]/10 flex items-center justify-center mx-auto mb-4">
          <ClipboardList className="w-8 h-8 text-[#C9A962]" />
        </div>
        <h3 className={`text-lg font-medium ${theme.text} mb-2`}>¿Necesitas una cotización?</h3>
        <p className={`text-sm ${theme.textSecondary} mb-6`}>
          Selecciona los productos que te interesan, agrega los detalles y consulta directamente con nuestro equipo por WhatsApp.
        </p>
        
        <button
          onClick={() => setShowForm(true)}
          data-testid="btn-nueva-consulta"
          className="w-full py-4 rounded-xl font-semibold bg-gradient-to-r from-[#C9A962] to-[#D4AF37] text-black hover:from-[#D4AF37] hover:to-[#C9A962] transition-all flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Nueva consulta de cotización
        </button>
      </div>
      
      {/* Información adicional */}
      <div className={`${theme.card} border ${theme.border} rounded-2xl p-5`}>
        <h4 className={`font-medium ${theme.text} mb-3 flex items-center gap-2`}>
          <Info className="w-4 h-4 text-[#C9A962]" />
          ¿Cómo funciona?
        </h4>
        <ul className={`space-y-2 text-sm ${theme.textSecondary}`}>
          <li className="flex items-start gap-2">
            <span className="text-[#C9A962] font-bold">1.</span>
            Selecciona los productos que deseas cotizar
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#C9A962] font-bold">2.</span>
            Agrega detalles como colores, tallas o especificaciones
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#C9A962] font-bold">3.</span>
            Indica la fecha de tu evento si la conoces
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#C9A962] font-bold">4.</span>
            Consulta por WhatsApp y un asesor te contactará
          </li>
        </ul>
      </div>
    </div>
  );
}

// ==================== MOBILE NAVIGATION ====================
function MobileNavigation({ activeTab, onTabChange }) {
  const tabs = [
    { id: 'inicio', label: 'Inicio', icon: Home },
    { id: 'tienda', label: 'Tienda', icon: ShoppingBag },
    { id: 'cotizar', label: 'Cotizar', icon: ClipboardList },
    { id: 'pedidos', label: 'Pedidos', icon: Package },
    { id: 'puntos', label: 'Puntos', icon: Star },
    { id: 'perfil', label: 'Perfil', icon: User }
  ];
  
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-[#121212]/95 backdrop-blur-md border-t border-zinc-800 px-1 py-2 md:hidden safe-area-inset-bottom">
      <div className="flex justify-around items-center max-w-md mx-auto">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`flex flex-col items-center gap-1 px-2 py-2 rounded-xl transition-all ${
                isActive ? 'text-[#C9A962]' : 'text-zinc-500 hover:text-zinc-300'
              }`}
              data-testid={`nav-${tab.id}`}
            >
              <Icon className={`w-5 h-5 ${isActive ? 'fill-current' : ''}`} />
              <span className="text-[10px] font-medium">{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

// ==================== SIDEBAR NAVIGATION (DESKTOP) ====================
function SidebarNavigation({ activeTab, onTabChange, onLogout, perfil }) {
  const { isDark } = useTheme();
  const theme = getThemeColors(isDark);
  
  const tabs = [
    { id: 'inicio', label: 'Inicio', icon: Home },
    { id: 'tienda', label: 'Compra online', icon: ShoppingBag },
    { id: 'cotizar', label: 'Solicitar Cotización', icon: ClipboardList },
    { id: 'pedidos', label: 'Mis Pedidos', icon: Package },
    { id: 'puntos', label: 'Puntos JPM', icon: Star },
    { id: 'perfil', label: 'Mi Perfil', icon: User }
  ];
  
  return (
    <aside className={`hidden md:flex flex-col ${theme.sidebar} border-r ${theme.sidebarBorder} p-6 h-full`}>
      {/* Logo */}
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A962] to-[#8B7355] flex items-center justify-center">
            <span className="text-sm font-serif font-bold text-black">JPM</span>
          </div>
          <div>
            <p className="text-xs text-[#C9A962] tracking-[0.15em]">MI JPM</p>
            <p className={`text-sm ${theme.text} font-medium`}>Juan Pablo Murillo</p>
          </div>
        </div>
      </div>
      
      {/* User greeting */}
      <div className={`mb-6 pb-6 border-b ${theme.sidebarBorder}`}>
        <p className={`${theme.textSecondary} text-xs mb-1`}>Bienvenido,</p>
        <p className={`${theme.text} font-medium`}>{perfil?.nombre}</p>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 space-y-1">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                isActive 
                  ? 'bg-[#C9A962]/10 text-[#C9A962] border border-[#C9A962]/20' 
                  : `${theme.textSecondary} ${isDark ? 'hover:bg-zinc-800' : 'hover:bg-gray-100'} hover:${theme.text}`
              }`}
              data-testid={`sidebar-${tab.id}`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-sm font-medium">{tab.label}</span>
            </button>
          );
        })}
      </nav>
      
      {/* Theme Toggle + Logout */}
      <div className="space-y-2">
        <div className="flex items-center gap-3 px-4 py-3">
          <ThemeToggle />
          <span className="text-sm text-zinc-500">Tema</span>
        </div>
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-zinc-500 hover:bg-red-500/10 hover:text-red-400 transition-all"
          data-testid="sidebar-logout"
        >
          <LogOut className="w-5 h-5" />
          <span className="text-sm font-medium">Cerrar sesión</span>
        </button>
      </div>
    </aside>
  );
}

// ==================== DOCUMENT VIEWER ====================
function PedidoDocumento({ pedido, onBack, onContactar }) {
  const [downloading, setDownloading] = useState(false);
  
  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('mi_jpm_token');
      const response = await axios.get(`${API_URL}/api/mi-jpm/portal/pedido/${pedido.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Estado_Cuenta_${pedido.numero}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Documento descargado');
    } catch (error) {
      toast.error('Error al descargar el documento');
    } finally {
      setDownloading(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
        >
          <ChevronRight className="w-5 h-5 rotate-180" />
          <span>Volver</span>
        </button>
        <button
          onClick={downloadPDF}
          disabled={downloading}
          className="flex items-center gap-2 bg-[#C9A962] hover:bg-[#D4AF37] text-black px-4 py-2 rounded-xl font-medium transition-colors disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {downloading ? 'Descargando...' : 'Descargar PDF'}
        </button>
      </div>
      
      <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-6">
        <h2 className="text-xl font-medium text-white mb-6">Pedido {pedido.numero}</h2>
        
        {/* Order summary content */}
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-xs text-zinc-500 uppercase mb-1">Estado</p>
              <p className="text-white">{pedido.estado_venta || 'En proceso'}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500 uppercase mb-1">Fecha del evento</p>
              <p className="text-white">{formatDateLong(pedido.fecha_evento)}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500 uppercase mb-1">Entrega programada</p>
              <p className="text-white">{formatDateLong(pedido.fecha_entrega)}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500 uppercase mb-1">Total</p>
              <p className="text-white">{formatPrice(pedido.total_final)}</p>
            </div>
          </div>
          
          {/* Items */}
          {pedido.items && pedido.items.length > 0 && (
            <div>
              <p className="text-xs text-zinc-500 uppercase mb-3">Prendas y productos</p>
              <div className="space-y-2">
                {pedido.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between bg-zinc-800/50 rounded-lg px-4 py-3">
                    <span className="text-sm text-zinc-300">{item.descripcion || item.prenda}</span>
                    <span className="text-sm text-white">{formatPrice(item.valor_unitario || item.valor)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Contact */}
      <button
        onClick={onContactar}
        className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-4 rounded-xl transition-colors flex items-center justify-center gap-2"
      >
        <MessageCircle className="w-5 h-5" />
        ¿Necesitas ayuda? Contáctanos por WhatsApp
      </button>
    </div>
  );
}

// ==================== MAIN DASHBOARD COMPONENT ====================
function MiJPMDashboard({ perfil, onLogout }) {
  const { isDark } = useTheme();
  const [pedidos, setPedidos] = useState([]);
  const [puntos, setPuntos] = useState(null);
  const [puntosHistorial, setPuntosHistorial] = useState([]);
  const [historialLoading, setHistorialLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('inicio');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [showPuntosModal, setShowPuntosModal] = useState(false);
  
  // Get theme-aware colors
  const theme = getThemeColors(isDark);
  
  // Handler para cambiar de tab
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('mi_jpm_token');
      
      const [pedidosRes, puntosRes] = await Promise.all([
        axios.get(`${API_URL}/api/mi-jpm/portal/mis-pedidos`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/mi-jpm/portal/mis-puntos`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);
      
      setPedidos(pedidosRes.data.pedidos || []);
      setPuntos(puntosRes.data);
      
      // Auto-expand first order
      if (pedidosRes.data.pedidos?.length > 0) {
        setExpandedOrder(pedidosRes.data.pedidos[0].id);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        onLogout();
      } else {
        toast.error('Error al cargar tus datos');
      }
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Actualiza en silencio la lista si el equipo elimina o cambia una venta mientras el portal está abierto
  const pedidoAbiertoRef = useRef(null);
  pedidoAbiertoRef.current = selectedPedido;
  const refrescarPedidos = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('mi_jpm_token')}` };
      const [pedidosRes, puntosRes] = await Promise.all([
        axios.get(`${API_URL}/api/mi-jpm/portal/mis-pedidos`, { headers }),
        axios.get(`${API_URL}/api/mi-jpm/portal/mis-puntos`, { headers }).catch(() => null),
      ]);
      const nuevos = pedidosRes.data.pedidos || [];
      const ids = new Set(nuevos.map(p => p.id));
      setPedidos(prev => (JSON.stringify(prev) === JSON.stringify(nuevos) ? prev : nuevos));
      if (puntosRes) setPuntos(prev => (JSON.stringify(prev) === JSON.stringify(puntosRes.data) ? prev : puntosRes.data));
      setExpandedOrder(prev => (prev && !ids.has(prev) ? (nuevos[0]?.id || null) : prev));
      if (pedidoAbiertoRef.current && !ids.has(pedidoAbiertoRef.current.id)) {
        setSelectedPedido(null);
      }
    } catch (error) {
      if (error.response?.status === 401) onLogout();
    }
  }, [onLogout]);

  useEffect(() => {
    const revisar = () => { if (document.visibilityState === 'visible') refrescarPedidos(); };
    const timer = setInterval(revisar, 30000);
    document.addEventListener('visibilitychange', revisar);
    window.addEventListener('focus', revisar);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', revisar);
      window.removeEventListener('focus', revisar);
    };
  }, [refrescarPedidos]);

  // Fetch points history when switching to puntos tab
  useEffect(() => {
    if (activeTab === 'puntos' && puntosHistorial.length === 0 && !historialLoading) {
      const fetchHistorial = async () => {
        setHistorialLoading(true);
        try {
          const token = localStorage.getItem('mi_jpm_token');
          const response = await axios.get(`${API_URL}/api/mi-jpm/portal/mis-puntos/historial`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setPuntosHistorial(response.data.historial || []);
        } catch (error) {
          console.error('Error fetching history:', error);
        } finally {
          setHistorialLoading(false);
        }
      };
      fetchHistorial();
    }
  }, [activeTab, puntosHistorial.length, historialLoading]);

  const contactarJPM = async () => {
    try {
      const token = localStorage.getItem('mi_jpm_token');
      const response = await axios.get(`${API_URL}/api/mi-jpm/portal/whatsapp-jpm`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.open(response.data.whatsapp_url, '_blank');
    } catch (error) {
      toast.error('Error al abrir WhatsApp');
    }
  };

  // Calculate KPIs
  const pedidosActivos = pedidos.filter(p => !['entregada', 'cancelada'].includes((p.estado_venta || '').toLowerCase())).length;
  const proximoPedido = pedidos.find(p => p.fecha_entrega);
  const diasProximaEntrega = proximoPedido ? calcularDiasRestantes(proximoPedido.fecha_entrega) : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-[#C9A962]/30 border-t-[#C9A962] rounded-full animate-spin" />
          <p className="text-zinc-400 text-sm">Cargando tu información...</p>
        </div>
      </div>
    );
  }

  if (selectedPedido) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto p-4 lg:p-8">
          <PedidoDocumento 
            pedido={selectedPedido} 
            onBack={() => setSelectedPedido(null)}
            onContactar={contactarJPM}
          />
        </div>
      </div>
    );
  }

  // Render content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case 'pedidos':
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-serif font-medium text-white mb-4">Mis Pedidos</h2>
            {pedidos.length === 0 ? (
              <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-8 text-center">
                <Package className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-400">No tienes pedidos activos</p>
              </div>
            ) : (
              pedidos.map((pedido) => (
                <OrderCard 
                  key={pedido.id}
                  pedido={pedido}
                  onClick={setSelectedPedido}
                  expanded={expandedOrder === pedido.id}
                  onToggle={() => setExpandedOrder(expandedOrder === pedido.id ? null : pedido.id)}
                  onActualizar={refrescarPedidos}
                />
              ))
            )}
          </div>
        );
        
      case 'tienda':
        return <SeccionTienda claro={!isDark} onPedidoCreado={refrescarPedidos} onVerPedidos={() => setActiveTab('pedidos')} />;
        
      case 'puntos':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-serif font-medium text-white">Puntos JPM</h2>
            <PuntosSection puntos={puntos} onShowInfo={() => setShowPuntosModal(true)} />
            <TarjetaReferidos />
            
            {/* Historial de Puntos */}
            <div className="bg-[#18181B] border border-[#27272A] rounded-2xl p-5 lg:p-6">
              <div className="flex items-center gap-3 mb-4">
                <History className="w-5 h-5 text-[#C9A962]" />
                <h3 className="text-lg font-medium text-white">Historial de Movimientos</h3>
              </div>
              <PuntosHistorial historial={puntosHistorial} loading={historialLoading} />
            </div>
          </div>
        );
        
      case 'perfil':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-serif font-medium text-white">Mi Perfil</h2>
            <ProfileSection perfil={perfil} />
          </div>
        );
      
      case 'cotizar':
        return <SeccionCotizar perfil={perfil} />;
        
      default: // inicio
        return (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4 mb-6">
              <KPICard
                icon={Package}
                label="Pedidos activos"
                value={pedidosActivos}
                subvalue={pedidosActivos === 1 ? "pedido en proceso" : "pedidos en proceso"}
                onClick={() => setActiveTab('pedidos')}
                testId="kpi-pedidos"
              />
              <KPICard
                icon={Calendar}
                label="Próxima entrega"
                value={diasProximaEntrega !== null && diasProximaEntrega >= 0 ? `${diasProximaEntrega} días` : 'Sin fecha'}
                subvalue={proximoPedido ? formatDate(proximoPedido.fecha_entrega) : null}
                highlight={diasProximaEntrega !== null && diasProximaEntrega <= 14}
                onClick={proximoPedido ? () => setActiveTab('pedidos') : null}
                testId="kpi-entrega"
              />
              <KPICard
                icon={Star}
                label="Puntos JPM"
                value={puntos?.puntos_disponibles || 0}
                subvalue={puntos?.puntos_disponibles > 0 ? `${formatPrice(puntos.valor_beneficio)} disponibles` : null}
                highlight={puntos?.puntos_disponibles > 0}
                onClick={() => setActiveTab('puntos')}
                testId="kpi-puntos"
              />
            </div>
            
            {/* Active Orders */}
            {pedidos.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-serif font-medium text-white flex items-center gap-2">
                    <Package className="w-5 h-5 text-[#C9A962]" />
                    Tu Pedido Activo
                  </h2>
                  {pedidos.length > 1 && (
                    <button 
                      onClick={() => setActiveTab('pedidos')}
                      className="text-xs text-[#C9A962] hover:underline"
                    >
                      Ver todos ({pedidos.length})
                    </button>
                  )}
                </div>
                <OrderCard 
                  pedido={pedidos[0]}
                  onClick={setSelectedPedido}
                  expanded={expandedOrder === pedidos[0].id}
                  onToggle={() => setExpandedOrder(expandedOrder === pedidos[0].id ? null : pedidos[0].id)}
                  onActualizar={refrescarPedidos}
                />
              </div>
            )}
            
            {/* Points Section (if has points) */}
            {puntos?.programa_activo && puntos?.puntos_disponibles > 0 && (
              <div className="mb-6">
                <PuntosSection puntos={puntos} onShowInfo={() => setShowPuntosModal(true)} />
              </div>
            )}
            <div className="mb-6"><TarjetaReferidos /></div>
          </>
        );
    }
  };

  return (
    <div className={`min-h-screen ${theme.bg} flex`} data-testid="mi-jpm-dashboard">
      {/* Sidebar - Desktop only */}
      <SidebarNavigation 
        activeTab={activeTab} 
        onTabChange={handleTabChange} 
        onLogout={onLogout}
        perfil={perfil}
      />
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile Header */}
        <header className={`md:hidden ${theme.bgSecondary} border-b ${theme.sidebarBorder} px-4 py-4 sticky top-0 z-40`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#C9A962] to-[#8B7355] flex items-center justify-center">
                <span className="text-xs font-serif font-bold text-black">JPM</span>
              </div>
              <div>
                <p className="text-[10px] text-[#C9A962] tracking-[0.15em]">MI JPM</p>
                <p className={`text-sm ${theme.text} font-medium`}>{perfil?.nombre?.split(' ')[0]}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <button
                onClick={onLogout}
                className={`p-2 ${theme.textSecondary} hover:${theme.text} transition-colors`}
                data-testid="mi-jpm-logout-button"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </header>
        
        {/* Content Area */}
        <main className="mijpm-main flex-1 p-4 lg:p-8 pb-24 md:pb-8 max-w-[1440px] mx-auto w-full">
          {/* Desktop Welcome with Theme Toggle */}
          <div className="hidden md:flex md:items-start md:justify-between mb-8">
            <div>
              <p className={`${theme.textSecondary} text-sm`}>Hola,</p>
              <h1 className={`text-3xl font-serif font-medium ${theme.text}`}>{perfil?.nombre}</h1>
              <p className="text-[#C9A962] text-sm mt-1">Bienvenido a tu área privada</p>
            </div>
            <ThemeToggle />
          </div>
          
          {/* Mobile Welcome */}
          <div className="md:hidden mb-6">
            <h1 className={`text-xl font-medium ${theme.text}`}>Hola, {perfil?.nombre?.split(' ')[0]}</h1>
            <p className="text-zinc-400 text-sm">Bienvenido a Mi JPM</p>
          </div>
          
          {renderContent()}
        </main>
        
        {/* WhatsApp Floating Button */}
        <button
          onClick={contactarJPM}
          className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-40 w-14 h-14 bg-emerald-600 hover:bg-emerald-500 rounded-full shadow-lg shadow-emerald-900/30 flex items-center justify-center transition-all hover:scale-105"
          data-testid="whatsapp-float-button"
        >
          <MessageCircle className="w-6 h-6 text-white" />
        </button>
      </div>
      
      {/* Mobile Navigation */}
      <MobileNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      
      {/* Points Modal */}
      <PuntosModal isOpen={showPuntosModal} onClose={() => setShowPuntosModal(false)} />
    </div>
  );
}

// ==================== THEME TOGGLE COMPONENT ====================
function ThemeToggle() {
  const { isDark, toggleTheme } = useTheme();
  
  return (
    <button
      onClick={toggleTheme}
      className={`p-2 rounded-lg transition-all ${
        isDark 
          ? 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white' 
          : 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900'
      }`}
      data-testid="theme-toggle"
      title={isDark ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
    >
      {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
    </button>
  );
}

// ==================== MAIN COMPONENT ====================
export default function MiJPMPortal() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [perfil, setPerfil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('mi_jpm_theme');
    return saved ? saved === 'dark' : true; // Default to dark
  });

  const toggleTheme = () => {
    setIsDark(prev => {
      const newTheme = !prev;
      localStorage.setItem('mi_jpm_theme', newTheme ? 'dark' : 'light');
      return newTheme;
    });
  };

  useEffect(() => {
    const token = localStorage.getItem('mi_jpm_token');
    if (token) {
      // Verify token and load profile
      axios.get(`${API_URL}/api/mi-jpm/portal/mi-perfil`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(response => {
          setPerfil(response.data);
          setIsAuthenticated(true);
        })
        .catch(() => {
          localStorage.removeItem('mi_jpm_token');
          localStorage.removeItem('mi_jpm_nombre');
          localStorage.removeItem('mi_jpm_cliente_id');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (data) => {
    setIsAuthenticated(true);
    // Fetch full profile
    axios.get(`${API_URL}/api/mi-jpm/portal/mi-perfil`, {
      headers: { Authorization: `Bearer ${data.access_token}` }
    })
      .then(response => setPerfil(response.data))
      .catch(console.error);
  };

  const handleLogout = () => {
    localStorage.removeItem('mi_jpm_token');
    localStorage.removeItem('mi_jpm_nombre');
    localStorage.removeItem('mi_jpm_cliente_id');
    setIsAuthenticated(false);
    setPerfil(null);
    toast.success('Sesión cerrada');
  };

  const themeValue = { isDark, toggleTheme };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-[#C9A962]/30 border-t-[#C9A962] rounded-full animate-spin" />
          <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>Cargando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <ThemeContext.Provider value={themeValue}>
        <MiJPMLogin onLogin={handleLogin} />
      </ThemeContext.Provider>
    );
  }

  return (
    <ThemeContext.Provider value={themeValue}>
      <MiJPMDashboard perfil={perfil} onLogout={handleLogout} />
    </ThemeContext.Provider>
  );
}
